import {
  ACTIVITY_STATUS_LABEL,
  AUTHORITY_LABEL,
  CLAIM_STATUS_LABEL,
  COVERAGE_STATE_LABEL,
  ISSUE_STATE_LABEL,
  TIER_LABEL,
  type ActivityStatus,
  type AuthorityVerdict,
  type ClaimStatus,
  type CoverageState,
  type ControlOutcome,
  type IssueState,
  type Severity,
  type Tier,
  type TraceOutcome,
} from "@ti/core";
import {
  AlertOctagon,
  AlertTriangle,
  ArrowDownCircle,
  Banknote,
  FileCheck2,
  FileText,
  Search,
  Siren,
  CheckCircle2,
  CircleDashed,
  CircleDot,
  Clock,
  EyeOff,
  FlaskConical,
  Hand,
  Info,
  RotateCcw,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  ShieldQuestion,
  XCircle,
} from "lucide-react";
import type { ReactNode } from "react";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/* Status colors are reserved for state and always ship with an icon + label. */

export const SEVERITY_TONE: Record<Severity, BadgeTone> = { critical: "critical", high: "serious", medium: "warning", low: "neutral" };
export const SEVERITY_COLOR: Record<Severity, string> = { critical: "var(--critical)", high: "var(--serious)", medium: "var(--warning)", low: "var(--neutral-status)" };
const SEVERITY_ICON: Record<Severity, ReactNode> = { critical: <AlertOctagon />, high: <AlertTriangle />, medium: <CircleDot />, low: <Info /> };

export function SeverityPill({ severity, className }: { severity: Severity; className?: string }) {
  return (
    <Badge tone={SEVERITY_TONE[severity]} className={cn("capitalize", className)}>
      {SEVERITY_ICON[severity]}
      {severity}
    </Badge>
  );
}

export function SeverityDot({ severity }: { severity: Severity }) {
  return <span aria-label={severity} className="inline-block size-2 shrink-0 rounded-full" style={{ background: SEVERITY_COLOR[severity] }} />;
}

export const COVERAGE_TONE: Record<CoverageState, BadgeTone> = {
  covered: "good",
  grace: "warning",
  mitigated: "info",
  verifying: "violet",
  graduated: "serious",
  excluded: "critical",
  unobserved: "neutral",
};
export const COVERAGE_COLOR: Record<CoverageState, string> = {
  covered: "var(--good)",
  grace: "var(--warning)",
  mitigated: "var(--info)",
  verifying: "var(--violet)",
  graduated: "var(--serious)",
  excluded: "var(--critical)",
  unobserved: "var(--neutral-status)",
};
const COVERAGE_ICON: Record<CoverageState, ReactNode> = {
  covered: <ShieldCheck />,
  grace: <Clock />,
  mitigated: <ShieldAlert />,
  verifying: <FlaskConical />,
  graduated: <ArrowDownCircle />,
  excluded: <ShieldOff />,
  unobserved: <EyeOff />,
};
const COVERAGE_HELP: Record<CoverageState, string> = {
  covered: "Monitored with a clean or verified track record.",
  grace: "Issue flagged — still covered while the builder fixes it.",
  mitigated: "Neutralized by a control the agent platform enforces — stays covered while the control is active.",
  verifying: "Fix declared — replay, probes and production soak in progress.",
  graduated: "Grace expired — reduced sub-limit with co-insurance until remediated.",
  excluded: "Known issue not fixed in time — losses not covered until re-verified.",
  unobserved: "No telemetry or unconfirmed classification — not covered.",
};

export function CoverageChip({ state, className, withHelp = true }: { state: CoverageState; className?: string; withHelp?: boolean }) {
  const chip = (
    <Badge tone={COVERAGE_TONE[state]} className={className}>
      {COVERAGE_ICON[state]}
      {COVERAGE_STATE_LABEL[state]}
    </Badge>
  );
  return withHelp ? <Tooltip content={COVERAGE_HELP[state]}>{chip}</Tooltip> : chip;
}

const ISSUE_TONE: Record<IssueState, BadgeTone> = {
  detected: "neutral",
  grace: "warning",
  mitigated: "info",
  verifying: "violet",
  resolved: "good",
  excluded: "critical",
  regressed: "serious",
};
const ISSUE_ICON: Record<IssueState, ReactNode> = {
  detected: <CircleDashed />,
  grace: <Clock />,
  mitigated: <ShieldAlert />,
  verifying: <FlaskConical />,
  resolved: <CheckCircle2 />,
  excluded: <ShieldOff />,
  regressed: <RotateCcw />,
};

export function IssueStateBadge({ state }: { state: IssueState }) {
  return (
    <Badge tone={ISSUE_TONE[state]}>
      {ISSUE_ICON[state]}
      {ISSUE_STATE_LABEL[state]}
    </Badge>
  );
}

const TIER_TONE: Record<Tier, string> = {
  T0: "border-border-strong text-fg-3",
  T1: "border-serious/50 text-serious-text",
  T2: "border-info/50 text-info-text",
  T3: "border-accent/50 text-accent",
  T4: "border-good/50 text-good-text",
};

export function TierBadge({ tier, showLabel = true, className }: { tier: Tier; showLabel?: boolean; className?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-md border px-1.5 py-px text-[11px] font-semibold", TIER_TONE[tier], className)}>
      {tier}
      {showLabel ? <span className="font-normal opacity-80">{TIER_LABEL[tier]}</span> : null}
    </span>
  );
}

const CONTROL: Record<ControlOutcome, { tone: BadgeTone; label: string; icon: ReactNode; help: string }> = {
  allowed: { tone: "good", label: "Allowed", icon: <CheckCircle2 />, help: "The platform's policy allowed the action." },
  approved: { tone: "info", label: "Approved", icon: <Hand />, help: "A person approved the action on the platform." },
  blocked: { tone: "critical", label: "Blocked", icon: <XCircle />, help: "The platform blocked the action. It never ran." },
};

/** Outcome of a control, as reported by the platform that executes the tool. */
export function ControlChip({ outcome, className, withHelp = true }: { outcome: ControlOutcome; className?: string; withHelp?: boolean }) {
  const v = CONTROL[outcome];
  const chip = (
    <Badge tone={v.tone} className={className}>
      {v.icon}
      {v.label}
    </Badge>
  );
  return withHelp ? <Tooltip content={v.help}>{chip}</Tooltip> : chip;
}

/** `platform:<id>` → the platform id; anything else (`agent`) is self-reported and not trusted. */
export function reporterLabel(reportedBy: string): { label: string; trusted: boolean } {
  return reportedBy.startsWith("platform:") && reportedBy.length > 9 ? { label: reportedBy.slice(9), trusted: true } : { label: "Agent (self-reported)", trusted: false };
}

const ACTIVITY_TONE: Record<ActivityStatus, BadgeTone> = { completed: "good", pending: "info", failed: "serious", blocked: "neutral" };
const ACTIVITY_ICON: Record<ActivityStatus, ReactNode> = { completed: <CheckCircle2 />, pending: <Clock />, failed: <XCircle />, blocked: <Hand /> };

export function ActivityStatusChip({ status }: { status: ActivityStatus }) {
  return (
    <Badge tone={ACTIVITY_TONE[status]}>
      {ACTIVITY_ICON[status]}
      {ACTIVITY_STATUS_LABEL[status]}
    </Badge>
  );
}

const AUTHORITY_TONE: Record<AuthorityVerdict, BadgeTone> = { within: "good", outside: "critical", unknown: "neutral" };
const AUTHORITY_ICON: Record<AuthorityVerdict, ReactNode> = { within: <ShieldCheck />, outside: <ShieldOff />, unknown: <ShieldQuestion /> };
const AUTHORITY_SHORT: Record<AuthorityVerdict, string> = { within: "Within", outside: "Outside", unknown: "Unknown" };

/** Whether an action or statement was within the agent's authority profile. */
export function AuthorityChip({ verdict, short }: { verdict: AuthorityVerdict; short?: boolean }) {
  return (
    <Badge tone={AUTHORITY_TONE[verdict]} title={AUTHORITY_LABEL[verdict]}>
      {AUTHORITY_ICON[verdict]}
      {short ? AUTHORITY_SHORT[verdict] : AUTHORITY_LABEL[verdict]}
    </Badge>
  );
}

const CLAIM_TONE: Record<ClaimStatus, BadgeTone> = {
  prompted: "warning",
  filed: "info",
  evidence_ready: "info",
  under_review: "violet",
  approved: "good",
  paid: "good",
  denied: "critical",
};

const CLAIM_ICON: Record<ClaimStatus, ReactNode> = {
  prompted: <Siren />,
  filed: <FileText />,
  evidence_ready: <FileCheck2 />,
  under_review: <Search />,
  approved: <CheckCircle2 />,
  paid: <Banknote />,
  denied: <XCircle />,
};

export function ClaimStatusBadge({ status }: { status: ClaimStatus }) {
  return (
    <Badge tone={CLAIM_TONE[status]}>
      {CLAIM_ICON[status]}
      {CLAIM_STATUS_LABEL[status]}
    </Badge>
  );
}

const OUTCOME: Record<TraceOutcome, { tone: BadgeTone; label: string }> = {
  success: { tone: "good", label: "Success" },
  failure: { tone: "critical", label: "Failure" },
  escalated: { tone: "info", label: "Escalated" },
  abandoned: { tone: "neutral", label: "Abandoned" },
};

export function OutcomeBadge({ outcome }: { outcome: TraceOutcome }) {
  return <Badge tone={OUTCOME[outcome].tone}>{OUTCOME[outcome].label}</Badge>;
}

export function ConfidenceBadge({ value }: { value: number }) {
  const tone: BadgeTone = value >= 0.9 ? "good" : value >= 0.75 ? "info" : "warning";
  return <Badge tone={tone} className="tabular">{Math.round(value * 100)}%</Badge>;
}
